import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { PackageSearch, Search, CheckCircle2, Circle, Truck, ArrowLeft, Loader2, Megaphone } from "lucide-react";
import { trackOrder, img } from "../lib/api";
import { useSite } from "../context/SiteContext";

const Track = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { settings } = useSite();
  const carriers = settings?.carriers || [];
  const [orderId, setOrderId] = useState(params.get("order") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (id) => {
    const q = (id || orderId).trim();
    if (!q) return;
    setLoading(true); setError(""); setData(null);
    try {
      const res = await trackOrder(q);
      setData(res);
    } catch (e) {
      setError("No order found with that ID. Please check and try again.");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("order")) lookup(params.get("order")); /* eslint-disable-next-line */ }, []);

  const carrier = data && carriers.find((c) => c.name.toLowerCase() === (data.carrier || "").toLowerCase());
  const stages = data?.stages || [];
  const currentIdx = data ? stages.indexOf(data.trackingStage) : -1;

  return (
    <div className="bg-navy min-h-screen py-10">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-slate-300 hover:text-gold transition-colors mb-8"><ArrowLeft size={18} /> Home</button>
        <div className="text-center mb-8">
          <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-gold-light to-gold-dark flex items-center justify-center text-navy mx-auto mb-4"><PackageSearch size={26} /></div>
          <h1 className="text-3xl font-bold text-white font-display">Track Your Order</h1>
          <p className="text-slate-400 mt-2">Enter your order ID (e.g. BB-AB12CD) to see live status.</p>
        </div>

        <div className="flex gap-3 max-w-lg mx-auto mb-10">
          <input value={orderId} onChange={(e) => setOrderId(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && lookup()} placeholder="BB-XXXXXX" className="flex-1 bg-navy-700 border border-white/15 focus:border-gold rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-colors" />
          <button onClick={() => lookup()} disabled={loading} className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-navy font-semibold px-5 py-3 rounded-xl transition-colors disabled:opacity-60">{loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />} Track</button>
        </div>

        {error && <p className="text-center text-red-400">{error}</p>}

        {data && (
          <div className="space-y-6">
            <div className="bg-navy-800 rounded-2xl border border-white/10 p-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-slate-400 text-sm">Order</p>
                  <p className="text-gold font-display font-bold text-xl">{data.orderId}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-400 text-sm">Status</p>
                  <p className="text-white font-semibold">{data.trackingStage}</p>
                </div>
              </div>
              {(data.carrier || data.trackingNumber) && (
                <div className="mt-5 flex items-center gap-4 bg-navy-700 rounded-xl p-4">
                  {carrier?.logo ? <img src={img(carrier.logo)} alt={carrier.name} className="h-8" /> : <Truck size={22} className="text-gold" />}
                  <div>
                    <p className="text-white font-medium">{data.carrier || "Carrier pending"}</p>
                    {data.trackingNumber && <p className="text-slate-400 text-sm">Tracking #: <span className="text-gold">{data.trackingNumber}</span></p>}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-navy-800 rounded-2xl border border-white/10 p-6">
              <h3 className="text-white font-bold mb-6">Shipment Progress</h3>
              <div className="space-y-0">
                {stages.map((st, i) => {
                  const done = i <= currentIdx;
                  const isCurrent = i === currentIdx;
                  return (
                    <div key={st} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        {done ? <CheckCircle2 size={22} className={isCurrent ? "text-gold" : "text-emerald-400"} /> : <Circle size={22} className="text-navy-600" />}
                        {i < stages.length - 1 && <div className={`w-px flex-1 min-h-[28px] ${i < currentIdx ? "bg-emerald-400/60" : "bg-white/10"}`} />}
                      </div>
                      <div className={`pb-6 ${done ? "" : "opacity-50"}`}>
                        <p className={`font-medium ${isCurrent ? "text-gold" : "text-white"}`}>{st}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {data.customerUpdates?.length > 0 && (
              <div className="glass-gold-card rounded-2xl p-6" data-testid="track-customer-updates">
                <h3 className="text-white font-bold mb-5 inline-flex items-center gap-2"><Megaphone size={18} className="text-gold" /> Messages from {settings?.brandName || "us"}</h3>
                <div className="space-y-4">
                  {[...data.customerUpdates].reverse().map((u, i) => (
                    <div key={i} className="bg-navy/60 rounded-xl border border-gold/20 p-4" data-testid={`track-update-${i}`}>
                      <p className="text-slate-200 text-sm whitespace-pre-wrap">{u.message}</p>
                      <p className="text-slate-500 text-xs mt-2">{u.at ? new Date(u.at).toLocaleString() : ""}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.trackingHistory?.length > 0 && (
              <div className="bg-navy-800 rounded-2xl border border-white/10 p-6">
                <h3 className="text-white font-bold mb-5">Updates</h3>
                <div className="space-y-4">
                  {[...data.trackingHistory].reverse().map((h, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="h-2 w-2 rounded-full bg-gold mt-2 shrink-0" />
                      <div>
                        <p className="text-white text-sm font-medium">{h.stage}</p>
                        <p className="text-slate-400 text-sm">{h.note}</p>
                        <p className="text-slate-600 text-xs mt-0.5">{h.at ? new Date(h.at).toLocaleString() : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Track;
